import { AuthService } from 'src/app/base/services/auth.service';
import { ImportConfigurationClient, ImportConfigurationDTO, ClientDTO, FilterOperator, ImportConfigurationType, ImportConfigurationClientAccessType } from 'src/app/base/models/models.swagger.generated';
import { ErrorType } from 'src/app/core/routing/error-types';
import { tap, catchError, finalize } from 'rxjs/operators';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { Observable, throwError } from 'rxjs';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { FilterDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from '../../../base/shared/syncfusion/adaptor-service.interface';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImportConfigurationService extends ShowInactiveService implements IAdaptorService{
  public selectedClient: ClientDTO = null;

  private oneTimeFilter: FilterDTO;
  constructor(
    private importConfigurationClient: ImportConfigurationClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private authService: AuthService
    ) {
      super();
    }

  public setOneTimeFilter(filter: FilterDTO) {
    this.oneTimeFilter = filter;
  }

  getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    const client = this.authService.getUserInfo()?.client;
    if (client) {
      this.selectedClient = client;
    }
    if (!filters) {
      filters = [];
    }

    filters.push(new FilterDTO({ isComplex: false, field: 'type', operator: FilterOperator.Equal, value:  ImportConfigurationType.Configuration.toString()}));
    filters.push(new FilterDTO({ isComplex: false, field: 'clientAccessType', operator: FilterOperator.Equal, value: ImportConfigurationClientAccessType.Client.toString()}));
    if (this.oneTimeFilter) {
      filters.push(this.oneTimeFilter);
    }
    return this.importConfigurationClient.searchImportConfigurations(this.selectedClient?.id, skip, take, sortBy, sortDirection, filters)
      .pipe(
        tap(() => {
          this.oneTimeFilter = null;
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  addRecord(data: ImportConfigurationDTO): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.createImportConfiguration(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.add-title',
          'i18n.domain.import-configuration.messages.add-success-message');
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => err);
      })
    );
  }

  updateRecord(data: ImportConfigurationDTO): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.updateImportConfiguration(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.update-title',
          'i18n.domain.import-configuration.messages.update-success-message');
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => err);
      })
    );
  }

  deleteRecord(id: number): Observable<void> {
    return this.importConfigurationClient.deleteImportConfiguration(id)
    .pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.delete-title',
          'i18n.domain.import-configuration.messages.delete-success-message'
        );
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  getConfigurationById(id: number): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.getImportConfiguration(id).pipe(
      tap((config: ImportConfigurationDTO) => {
        config?.fileConfig?.fieldAssignments?.forEach(assignment => {
          if (assignment.fixValue != null) {
            assignment.importFunction = null;
          }
        });
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        this.toastService.showError(
          'i18n.errors.server-error.title',
          'i18n.errors.server-error.message', null, ErrorType.serverError);
        return throwError(() => err);
      })
    );
  }
}
